import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import DeviceFormMode from "@/components/DeviceFormMode";
import PDFFormViewer from "@/components/PDFFormViewer";
import { AlertCircle, Clock, CheckCircle2, Loader2 } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const MESSAGES = {
  invalid: {
    icon: AlertCircle,
    title: "This form link isn't valid",
    body: "The link may have been mistyped or replaced by a newer one. Please contact your clinic and ask them to send you a fresh link.",
    testid: "public-form-invalid",
    tone: "text-red-500 bg-red-50",
  },
  expired: {
    icon: Clock,
    title: "This form link has expired",
    body: "For your security, form links expire after a while. Please contact your clinic to request a new link.",
    testid: "public-form-expired",
    tone: "text-amber-600 bg-amber-50",
  },
  completed: {
    icon: CheckCircle2,
    title: "This form has already been submitted",
    body: "Thank you — there is nothing more to do. If you need to change an answer, please contact your clinic.",
    testid: "public-form-completed",
    tone: "text-[#205B5A] bg-[#205B5A]/10",
  },
  error: {
    icon: AlertCircle,
    title: "We couldn't load this form",
    body: "Something went wrong on our side. Please try again in a few moments, or contact your clinic.",
    testid: "public-form-error",
    tone: "text-red-500 bg-red-50",
  },
};

function StatusScreen({ state, clinicName }) {
  const cfg = MESSAGES[state] || MESSAGES.error;
  const Icon = cfg.icon;
  return (
    <div
      className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-5 py-16"
      data-testid={cfg.testid}
    >
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8 text-left shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-5 ${cfg.tone}`}>
          <Icon className="w-6 h-6" />
        </div>
        <h1 className="text-xl md:text-2xl text-[#1A1B1A] mb-3" style={H}>
          {cfg.title}
        </h1>
        <p className="text-sm text-[#4A4B4A] leading-relaxed">{cfg.body}</p>
        {clinicName && (
          <p className="text-xs text-[#9A9B9A] mt-6 pt-5 border-t border-[#1A1B1A]/[0.06]">
            Sent by {clinicName}
          </p>
        )}
      </div>
    </div>
  );
}

export default function PublicFormPage() {
  const { token: routeToken } = useParams();
  const [searchParams] = useSearchParams();
  const token = routeToken || searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null); // valid | invalid | expired | completed | error
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token) {
        setState("invalid");
        setLoading(false);
        return;
      }
      try {
        const res = await publicApi.get(`/public/forms/${token}`);
        if (cancelled) return;
        setData(res.data);
        setState(res.data.status);
      } catch (err) {
        if (!cancelled) setState("error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submitForm = useCallback(
    async (payload) => {
      await publicApi.post(`/public/forms/${token}/submit`, {
        responses: payload.responses,
        signature_type: payload.signature_type,
        signature_data: payload.signature_data,
        typed_signature: payload.typed_signature,
        guardian: payload.guardian || null,
      });
    },
    [token]
  );

  if (loading) {
    return (
      <div
        className="min-h-screen bg-[#FAF9F6] flex flex-col items-center justify-center gap-3"
        data-testid="public-form-loading"
      >
        <Loader2 className="w-6 h-6 text-[#205B5A] animate-spin" />
        <p className="text-sm text-[#757675]">Loading your form...</p>
      </div>
    );
  }

  if (state !== "valid") {
    return <StatusScreen state={state} clinicName={data?.clinic?.name} />;
  }

  const template = data.template;
  const shared = {
    template,
    patientName: data.patient_name,
    clinicName: data.clinic?.name,
    clinicLogo: data.clinic?.logo_url,
    submitForm,
    guardianSection: data.guardian_section,
    successNote: "You can now close this page.",
    autoCloseOnSuccess: false,
    onCancel: null,
    onComplete: null,
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="public-form-page">
      {template.is_uploaded_pdf ? (
        <PDFFormViewer {...shared} pdfFetchPath={`/public/forms/${token}/pdf`} pdfClient={publicApi} />
      ) : (
        <DeviceFormMode {...shared} />
      )}
    </div>
  );
}
